import { useEffect } from "react"

/**
 * Scroll-reveal hook.
 * Observes every element with a `data-reveal` attribute inside the given root
 * and adds the `is-visible` class once it enters the viewport.
 * Elements rendered later (e.g. after data loads) are picked up by a MutationObserver.
 */
export function useReveal(rootId: string): void {
    useEffect(() => {
        const root = document.getElementById(rootId)
        if (!root) return

        // No IntersectionObserver (old browsers / jsdom) → show everything immediately
        if (typeof IntersectionObserver === "undefined") {
            root.querySelectorAll<HTMLElement>("[data-reveal]").forEach(el => el.classList.add("is-visible"))
            return
        }

        const io = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("is-visible")
                        io.unobserve(entry.target)
                    }
                })
            },
            { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
        )

        const observeAll = () => {
            root.querySelectorAll<HTMLElement>("[data-reveal]:not(.is-visible)").forEach(el => io.observe(el))
        }

        observeAll()

        const mo = typeof MutationObserver !== "undefined"
            ? new MutationObserver(observeAll)
            : null
        mo?.observe(root, { childList: true, subtree: true })

        return () => {
            io.disconnect()
            mo?.disconnect()
        }
    }, [rootId])
}
